// The HUD panel slot: how a feature puts its own panel on the HUD (a quest tracker, a party strip, a clock, a status
// row) without the HUD importing the feature, and without a "HUD feature" importing every other one (rule 4). The
// HUD owns placement, order, visibility and fading; the panel owns what it draws inside the element it is given.
// @rpg/ui's HUD implements the slot.

/** The HUD panel slot's version. */
export const HUD_SLOT_VERSION = "1.0.0" as const;

/** Where on the screen a panel sits. Panels in one anchor stack away from the screen's edge. */
export type HudAnchor = "top-left" | "top-center" | "top-right" | "center-left" | "center-right" | "bottom-left" | "bottom-center" | "bottom-right";

/** Every anchor, in a fixed order. */
export const HUD_ANCHORS: readonly HudAnchor[] = ["top-left", "top-center", "top-right", "center-left", "center-right", "bottom-left", "bottom-center", "bottom-right"];

/** What the HUD tells a panel each frame. */
export interface HudFrame {
  /** Seconds (the HUD's clock). */
  now: number;
  /** The HUD is shown (false under screens and outside play: a panel may skip its work). */
  visible: boolean;
  /** The player asked for reduced motion. */
  reduceMotion: boolean;
}

/**
 * A panel a feature contributes. `Host` is the element type the HUD hands it (the DOM's `HTMLElement` for @rpg/ui's
 * HUD; a canvas HUD could hand something else).
 */
export interface HudPanel<Host = HTMLElement> {
  /** Unique on the HUD (`"quest-tracker"`). */
  id: string;
  anchor: HudAnchor;
  /** Order within its anchor: lower sits nearer the screen's edge. Default 0; ties keep the order panels were added. */
  priority?: number;
  /** Build into `host` once (an element the HUD made for this panel and placed). */
  mount(host: Host): void;
  /** Each HUD frame: write what changed (direct writes, not a re-render). Optional for a static panel. */
  update?(frame: HudFrame): void;
  /** Taken off the HUD: undo anything `mount` set up outside `host`. */
  dispose?(): void;
  /** Keep the HUD awake (not faded) while true, e.g. a timer running. */
  awake?(): boolean;
}

/** A panel as the slot reports it. */
export interface HudPanelInfo {
  id: string;
  anchor: HudAnchor;
  priority: number;
}

/** The slot a HUD offers. */
export interface HudPanelSlot<Host = HTMLElement> {
  /** Add a panel; returns the function that removes it. Adding an id twice throws. */
  addPanel(panel: HudPanel<Host>): () => void;
  /** The panels on the HUD, by anchor in `HUD_ANCHORS` order, then in their order within it. */
  panels(): HudPanelInfo[];
}

/** Order panels the way a slot shows them: by anchor, then priority (lower first), then the order they were added. */
export function orderPanels<P extends { anchor: HudAnchor; priority?: number }>(panels: readonly P[]): P[] {
  return panels
    .map((p, i) => ({ p, i }))
    .sort((a, b) => HUD_ANCHORS.indexOf(a.p.anchor) - HUD_ANCHORS.indexOf(b.p.anchor) || (a.p.priority ?? 0) - (b.p.priority ?? 0) || a.i - b.i)
    .map((x) => x.p);
}
